// ─────────────────────────────────────────────────────────────────────────────
// Appearance experiments for v2 — flip variations live and compare.
// Stored per browser (localStorage); applied as data-attributes on <html> so
// portaled layers (menus, sheets) pick them up too. Removed when v2 unmounts.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { Sheet, Segmented } from "./primitives";

const KEY = "eb-v2-appearance";
export const DEFAULTS = { backdrop: "sky", accent: "blue", buttons: "glass", density: "comfortable", icons: "duotone", type: "jakarta" };
const ATTRS = ["backdrop", "accent", "buttons", "density", "icons", "type"];

// Geometric faces close to the reference's Gilroy-like type. Each loads only
// when selected, and only while v2 is mounted (v1 never pays for them).
const FONTS = {
  jakarta: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400..800&display=swap",
  figtree: "https://fonts.googleapis.com/css2?family=Figtree:wght@400..800&display=swap",
  manrope: "https://fonts.googleapis.com/css2?family=Manrope:wght@400..800&display=swap",
};
export const ensureFont = (key) => {
  if (!FONTS[key] || document.querySelector(`link[data-v2-font="${key}"]`)) return;
  if (!document.querySelector('link[data-v2-font="pre"]')) {
    const pre = document.createElement("link");
    pre.rel = "preconnect"; pre.href = "https://fonts.gstatic.com"; pre.crossOrigin = "anonymous";
    pre.dataset.v2Font = "pre";
    document.head.appendChild(pre);
  }
  const link = document.createElement("link");
  link.rel = "stylesheet"; link.href = FONTS[key]; link.dataset.v2Font = key;
  document.head.appendChild(link);
};

export const BACKDROPS = [
  { value: "sky", label: "Sky" }, { value: "mist", label: "Mist" }, { value: "paper", label: "Paper" },
  { value: "dawn", label: "Dawn" }, { value: "aurora", label: "Aurora" }, { value: "lavender", label: "Lavender" },
  { value: "meadow", label: "Meadow" }, { value: "sand", label: "Sand" }, { value: "ocean", label: "Ocean" },
  { value: "smoke", label: "Smoke" },
];
const VALID = {
  backdrop: BACKDROPS.map((b) => b.value),
  icons: ["duotone", "soft", "vivid"],
  type: ["jakarta", "figtree", "manrope", "system"],
};
const load = () => {
  try {
    const v = { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY)) || {}) };
    Object.entries(VALID).forEach(([k, ok]) => { if (!ok.includes(v[k])) v[k] = DEFAULTS[k]; });
    return v;
  } catch { return DEFAULTS; }
};

export function useAppearance() {
  const [value, setValue] = useState(load);
  useEffect(() => { ensureFont(value.type); }, [value.type]);
  useEffect(() => {
    const root = document.documentElement;
    ATTRS.forEach((a) => root.setAttribute(`data-v2-${a}`, value[a]));
    try { localStorage.setItem(KEY, JSON.stringify(value)); } catch { /* ignore */ }
  }, [value]);
  useEffect(() => () => ATTRS.forEach((a) => document.documentElement.removeAttribute(`data-v2-${a}`)), []);
  return [value, (patch) => setValue((v) => ({ ...v, ...patch }))];
}

const ROWS = [
  {
    key: "backdrop", title: "Backdrop", note: "The atmosphere behind everything",
    swatches: BACKDROPS,
  },
  {
    key: "buttons", title: "Buttons", note: "Liquid glass refracts what's behind it",
    options: [{ value: "glass", label: "Liquid glass" }, { value: "solid", label: "Solid" }],
  },
  {
    key: "accent", title: "Accent", note: "Selection, links and primary actions",
    options: [{ value: "blue", label: "Blue" }, { value: "indigo", label: "Indigo" }, { value: "teal", label: "Teal" }, { value: "graphite", label: "Graphite" }],
  },
  {
    key: "icons", title: "Widget icons", note: "Duotone glyphs follow the accent colour",
    options: [{ value: "duotone", label: "Duotone" }, { value: "soft", label: "Soft tiles" }, { value: "vivid", label: "Glossy" }],
  },
  {
    key: "type", title: "Typeface", note: "Geometric faces in the spirit of the reference",
    options: [{ value: "jakarta", label: "Jakarta" }, { value: "figtree", label: "Figtree" }, { value: "manrope", label: "Manrope" }, { value: "system", label: "System" }],
  },
  {
    key: "density", title: "Density", note: "Spacing and card padding",
    options: [{ value: "comfortable", label: "Comfortable" }, { value: "compact", label: "Compact" }],
  },
];

export function AppearanceSheet({ open, onClose, value, onChange }) {
  return (
    <Sheet open={open} onClose={onClose} title="Appearance" subtitle="Experiments — changes apply instantly" width={580}
      footer={
        <>
          <button type="button" className="v2-btn" onClick={() => onChange(DEFAULTS)}>Reset</button>
          <button type="button" className="v2-btn v2-btn--primary" onClick={onClose}>Done</button>
        </>
      }>
      <div className="v2-look">
        {ROWS.map((r) => r.swatches ? (
          <div key={r.key} className="v2-look-row v2-look-row--stack">
            <div>
              <div className="v2-look-title">{r.title}</div>
              <div className="v2-look-note">{r.note}</div>
            </div>
            <div className="v2-look-swatches" role="radiogroup" aria-label={r.title}>
              {r.swatches.map((o) => (
                <button key={o.value} type="button" role="radio" aria-checked={value[r.key] === o.value}
                  className={`v2-look-swatch${value[r.key] === o.value ? " is-on" : ""}`} onClick={() => onChange({ [r.key]: o.value })}>
                  <span className="v2-bd" data-bd={o.value} aria-hidden="true"><i /></span>
                  <span>{o.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div key={r.key} className="v2-look-row">
            <div>
              <div className="v2-look-title">{r.title}</div>
              <div className="v2-look-note">{r.note}</div>
            </div>
            <Segmented size="sm" ariaLabel={r.title} options={r.options} value={value[r.key]}
              onChange={(v) => onChange({ [r.key]: v })} />
          </div>
        ))}
        <div className="v2-look-preview">
          <span className="v2-look-note">Preview</span>
          <div className="v2-look-buttons">
            <button type="button" className="v2-btn v2-btn--primary">Primary</button>
            <button type="button" className="v2-btn">Secondary</button>
            <button type="button" className="v2-icon-btn v2-glass" aria-label="Icon button">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            </button>
          </div>
        </div>
      </div>
    </Sheet>
  );
}
